import React, { useEffect, useState } from 'react';
import { X, ClipboardList, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/apiError';
import TagMultiSelect from './TagMultiSelect';

const inputCls = 'w-full px-2 py-1.5 rounded border text-sm bg-white';
const border = { borderColor: 'rgba(0,0,0,0.2)' };
export const STATUS_LABELS = { open: 'Open', in_progress: 'In progress', done: 'Done', cancelled: 'Cancelled' };

// Picks a member from the directory (committee/admin) — falls back to "me" for ordinary members.
export function MemberSelect({ value, onChange, allowOthers, testid = 'action-owner' }) {
  const { user, users, actionMembers } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!allowOthers) return;
    if (users && users.length) { setList(users.map((u) => ({ id: u.user_id, name: u.name || u.email }))); return; }
    actionMembers().then((d) => setList(d.map((u) => ({ id: u.user_id, name: u.name })))).catch(() => setList([]));
  }, [allowOthers, users, actionMembers]);
  if (!allowOthers) return <input value={user?.name || 'Me'} disabled className={`${inputCls} opacity-70`} style={border} data-testid={testid} />;
  const opts = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  if (user && !opts.some((o) => o.id === user.user_id)) opts.unshift({ id: user.user_id, name: `${user.name || user.email} (me)` });
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} style={border} data-testid={testid}>
      <option value="">— choose a member —</option>
      {opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  );
}

// Create or edit an action. `source` = {type, id, title} when created from a meeting/event.
export default function ActionFormModal({ action, source, onClose, onSaved }) {
  const { user, createAction, patchAction, isAdmin } = useAuth();
  const canAssign = isAdmin || (user && user.memberClass === 'committee');
  const [f, setF] = useState({
    title: action?.title || '', notes: action?.notes || '', owner_id: action?.owner_id || user?.user_id || '',
    due_date: action?.due_date || '', status: action?.status || 'open', tags: action?.tags || (source?.type === 'meeting' ? ['meeting'] : source?.type === 'event' ? ['events'] : []),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error('Please give the action a title.'); return; }
    setBusy(true);
    try {
      const r = action
        ? await patchAction(action.id, { title: f.title, notes: f.notes, owner_id: canAssign ? f.owner_id : undefined, due_date: f.due_date || '', status: f.status, tags: f.tags })
        : await createAction({ ...f, due_date: f.due_date || null, source_type: source?.type || 'manual', source_id: source?.id || null, source_title: source?.title || '' });
      toast.success(action ? 'Action updated.' : 'Action created.'); onSaved(r); onClose();
    } catch (e) { toast.error(formatApiError(e, 'Could not save the action.')); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose} data-testid="action-form-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <h3 className="font-title text-lg flex items-center gap-2" style={{ color: 'var(--maroon)' }}><ClipboardList size={17} /> {action ? 'Edit action' : 'New action'}</h3>
          <button onClick={onClose} data-testid="action-form-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          {source && <p className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--olive)' }} data-testid="action-form-source">From {source.type}: <b>{source.title}</b></p>}
          <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>What needs doing</label><input value={f.title} onChange={(e) => set('title', e.target.value)} className={inputCls} style={border} autoFocus data-testid="action-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Owner</label><MemberSelect value={f.owner_id} onChange={(v) => set('owner_id', v)} allowOthers={canAssign} /></div>
            <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Due date</label><input type="date" value={f.due_date || ''} onChange={(e) => set('due_date', e.target.value)} className={inputCls} style={border} data-testid="action-due" /></div>
          </div>
          <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Status</label>
            <select value={f.status} onChange={(e) => set('status', e.target.value)} className={inputCls} style={border} data-testid="action-status">{Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Tags</label><TagMultiSelect value={f.tags} onChange={(t) => set('tags', t)} area="actions" testid="action-tags" /></div>
          <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Notes</label><textarea rows={3} value={f.notes} onChange={(e) => set('notes', e.target.value)} className={inputCls} style={border} data-testid="action-notes" /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="action-save">{busy && <Loader2 size={14} className="animate-spin" />} {action ? 'Save' : 'Create action'}</button>
        </div>
      </div>
    </div>
  );
}
